import React, { useContext, useEffect, useMemo, useState } from "react";
import type { TableColumnsType } from "antd";
import { Checkbox, Select, Table, Typography } from "antd";
import {
  AddBulkScheduleSummary,
  BulkSchedule,
  DayScheduleValue,
  DeletedDraftSchedulePreview,
  UpdatedDraftSchedulePreview,
  ScheduleNormalized,
} from "./schedule-bulkadd-entity";
import { calculateTotalHours } from "../../../Util/calculation";
import { CompanyContext } from "../../../contexts/company-context";
import { TaskTemplateOperation } from "../../../serviceoperation/TaskTemplateOperation";
import { TaskTemplate } from "../../../types/taskTemplate";

interface DataType {
  key: React.Key;
  name: string;
  department: string;
  branch: string;
  createdAt: string;
  totalHours?: number;
}

type ExpandedDataType = {
  key: React.Key;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  description: string;
  hours: number;
};

const columns: TableColumnsType<DataType> = [
  { title: "Name", dataIndex: "name", key: "name" },
  { title: "Department", dataIndex: "department", key: "department" },
  { title: "Branch", dataIndex: "branch", key: "branch" },
  { title: "Created Date", dataIndex: "createdAt", key: "createdAt" },
  { title: "Total Hours", dataIndex: "totalHours", key: "totalHours" },
];

const deleteColumns: TableColumnsType<DeletedDraftSchedulePreview> = [
  { title: "Employee", dataIndex: "employeeName", key: "employeeName" },
  { title: "Date", dataIndex: "workDay", key: "workDay" },
  { title: "Start", dataIndex: "startTime", key: "startTime" },
  { title: "End", dataIndex: "endTime", key: "endTime" },
  { title: "Schedule ID", dataIndex: "scheduleId", key: "scheduleId" },
];

const updateColumns: TableColumnsType<UpdatedDraftSchedulePreview> = [
  { title: "Employee", dataIndex: "employeeName", key: "employeeName" },
  { title: "Date", dataIndex: "workDay", key: "workDay" },
  {
    title: "Original",
    dataIndex: "originalStartTime",
    key: "originalStartTime",
    render: (_: string, row) =>
      `${row.originalStartTime} - ${row.originalEndTime}`,
  },
  {
    title: "Updated",
    dataIndex: "updatedStartTime",
    key: "updatedStartTime",
    render: (_: string, row) =>
      `${row.updatedStartTime} - ${row.updatedEndTime}`,
  },
  { title: "Schedule ID", dataIndex: "scheduleId", key: "scheduleId" },
];

const expandedRowRender = (
  scheduleData: BulkSchedule[],
  parentKey: React.Key,
) => {
  const childColumns: TableColumnsType<ExpandedDataType> = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Start Time", dataIndex: "startTime", key: "startTime" },
    { title: "End Time", dataIndex: "endTime", key: "endTime" },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Hours", dataIndex: "hours", key: "hours" },
  ];

  const expandedData: ExpandedDataType[] = [];

  scheduleData
    .filter((item) => item.name === parentKey)
    .forEach((bulkSchedule) => {
      bulkSchedule.schedule.forEach((schedule) => {
        expandedData.push({
          key: bulkSchedule.key,
          date: schedule.date,
          startTime: `${schedule.startHour}:${schedule.startMin}`,
          endTime: `${schedule.endHour}:${schedule.endMin}`,
          title: schedule.title,
          description: schedule.description,
          hours: calculateTotalHours(
            `${schedule.startHour}:${schedule.startMin}`,
            `${schedule.endHour}:${schedule.endMin}`,
          ),
        });
      });
    });

  const totalHours = expandedData.reduce(
    (sum, record) => sum + record.hours,
    0,
  );

  const summaryRow = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}></Table.Summary.Cell>
      <Table.Summary.Cell index={1}></Table.Summary.Cell>
      <Table.Summary.Cell index={2}></Table.Summary.Cell>
      <Table.Summary.Cell index={3}></Table.Summary.Cell>
      <Table.Summary.Cell index={4}>Total Hours:</Table.Summary.Cell>
      <Table.Summary.Cell index={5}>{totalHours}</Table.Summary.Cell>
    </Table.Summary.Row>
  );

  return (
    <Table
      columns={childColumns}
      dataSource={expandedData}
      summary={summaryRow}
      pagination={false}
    />
  );
};

const ScheduleBulkAddSummary: React.FC<AddBulkScheduleSummary> = ({
  scheduleData,
  employeeDetails,
  selectedWeek,
  deletedDraftSchedules = [],
  updatedDraftSchedules = [],
  onSuccess,
}) => {
  const { Paragraph } = Typography;
  const [notifyEmployee, setNotifyEmployee] = useState<boolean>(false);
  const [selectedTaskTemplateId, setSelectedTaskTemplateId] = useState<
    string | null
  >(null);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const { selectedCompany } = useContext(CompanyContext);
  const taskTempOps = new TaskTemplateOperation();

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    taskTempOps
      .getTaskTemplateAsync(selectedCompany?.corporationId as number, 1, 255)
      .then((value) => {
        setTaskTemplates(
          value.results
            .filter((x) => x.activeInd)
            .map((x) => ({
              id: x.taskTemplateId.toString(),
              name: x.name,
              description: x.description,
              category: x.category,
              branchId: x.branchId,
              items: x.items || [],
              isDefault: x.isDefault,
              isActive: x.activeInd ?? false,
              createdAt: x.createdDateTimeUTC,
              updatedAt: x.createdDateTimeUTC,
            })),
        );
      });
  }, [selectedCompany]);

  const transformedData: BulkSchedule[] = scheduleData.map((item) => {
    const schedule: ScheduleNormalized[] = [];
    const addScheduleEntry = (
      day: string,
      startHour: string,
      startMin: string,
      endHour: string,
      endMin: string,
      scheduleId?: number,
    ) => {
      const branchId = employeeDetails.find((x) => x.employeeId === item.name)
        ?.organization?.branchId;

      if (branchId === undefined) {
        return;
      }

      schedule.push({
        date: day,
        startHour: startHour || "",
        startMin: startMin || "",
        endHour: endHour || "",
        endMin: endMin || "",
        title: "",
        description: "",
        branchId,
        scheduleId,
      });
    };

    const shouldIncludeDaySchedule = (daySchedule: DayScheduleValue) => {
      if (daySchedule[2]) {
        return false;
      }

      const scheduleId = daySchedule[3];
      const isModified = daySchedule[4] === true;

      if (scheduleId !== undefined) {
        return isModified;
      }

      return true;
    };

    if (item.schedulesunday && shouldIncludeDaySchedule(item.schedulesunday)) {
      addScheduleEntry(
        selectedWeek[0],
        item.schedulesunday[0]?.format("HH"),
        item.schedulesunday[0]?.format("mm"),
        item.schedulesunday[1]?.format("HH"),
        item.schedulesunday[1]?.format("mm"),
        item.schedulesunday[3],
      );
    }

    if (item.schedulemonday && shouldIncludeDaySchedule(item.schedulemonday)) {
      addScheduleEntry(
        selectedWeek[1],
        item.schedulemonday[0]?.format("HH"),
        item.schedulemonday[0]?.format("mm"),
        item.schedulemonday[1]?.format("HH"),
        item.schedulemonday[1]?.format("mm"),
        item.schedulemonday[3],
      );
    }

    if (
      item.scheduletuesday &&
      shouldIncludeDaySchedule(item.scheduletuesday)
    ) {
      addScheduleEntry(
        selectedWeek[2],
        item.scheduletuesday[0]?.format("HH"),
        item.scheduletuesday[0]?.format("mm"),
        item.scheduletuesday[1]?.format("HH"),
        item.scheduletuesday[1]?.format("mm"),
        item.scheduletuesday[3],
      );
    }

    if (
      item.schedulewednesday &&
      shouldIncludeDaySchedule(item.schedulewednesday)
    ) {
      addScheduleEntry(
        selectedWeek[3],
        item.schedulewednesday[0]?.format("HH"),
        item.schedulewednesday[0]?.format("mm"),
        item.schedulewednesday[1]?.format("HH"),
        item.schedulewednesday[1]?.format("mm"),
        item.schedulewednesday[3],
      );
    }

    if (
      item.schedulethursday &&
      shouldIncludeDaySchedule(item.schedulethursday)
    ) {
      addScheduleEntry(
        selectedWeek[4],
        item.schedulethursday[0]?.format("HH"),
        item.schedulethursday[0]?.format("mm"),
        item.schedulethursday[1]?.format("HH"),
        item.schedulethursday[1]?.format("mm"),
        item.schedulethursday[3],
      );
    }

    if (item.schedulefriday && shouldIncludeDaySchedule(item.schedulefriday)) {
      addScheduleEntry(
        selectedWeek[5],
        item.schedulefriday[0]?.format("HH"),
        item.schedulefriday[0]?.format("mm"),
        item.schedulefriday[1]?.format("HH"),
        item.schedulefriday[1]?.format("mm"),
        item.schedulefriday[3],
      );
    }

    if (
      item.schedulesaturday &&
      shouldIncludeDaySchedule(item.schedulesaturday)
    ) {
      addScheduleEntry(
        selectedWeek[6],
        item.schedulesaturday[0]?.format("HH"),
        item.schedulesaturday[0]?.format("mm"),
        item.schedulesaturday[1]?.format("HH"),
        item.schedulesaturday[1]?.format("mm"),
        item.schedulesaturday[3],
      );
    }
    return {
      key: item.key,
      name: item.name,
      schedule: schedule,
      isDraft: false,
      taskTemplateId: selectedTaskTemplateId ?? undefined,
    };
  });

  // After the data transformation
  // set the onSuccess to signal the parent componenet.
  const [onSuccessCalled, setOnSuccessCalled] = useState(false);

  useEffect(() => {
    if (!onSuccessCalled || notifyEmployee || selectedTaskTemplateId) {
      // Call onSuccess if it hasn't been called already
      onSuccess(
        transformedData,
        null,
        notifyEmployee,
        false,
        selectedTaskTemplateId || undefined,
      );
      setOnSuccessCalled(true); // Set the flag to true
    }
  }, [scheduleData, onSuccessCalled, notifyEmployee, selectedTaskTemplateId]);

  const handleExpand = (expanded: boolean, record: DataType) => {
    if (expanded) {
      // If expanded, add the key to the expandedRowKeys array
      setExpandedRowKeys((prevKeys) => [...prevKeys, record.key]);
    } else {
      // If collapsed, remove the key from the expandedRowKeys array
      setExpandedRowKeys((prevKeys) =>
        prevKeys.filter((key) => key !== record.key),
      );
    }
  };

  const rowData: DataType[] = useMemo(
    () =>
      employeeDetails
        .filter((emp) =>
          transformedData.some((td) => td.name === emp.employeeId),
        )
        .map((emp) => {
          const employeeSchedule = transformedData.find(
            (td) => td.name === emp.employeeId,
          );

          const totalHours =
            employeeSchedule?.schedule.reduce((sum, sched) => {
              const start = `${sched.startHour}:${sched.startMin}`;
              const end = `${sched.endHour}:${sched.endMin}`;
              return sum + calculateTotalHours(start, end);
            }, 0) ?? 0;

          return {
            key: emp.employeeId,
            name: `${emp.firstName} ${emp.lastName}`,
            department: emp.title,
            branch: emp.organization?.branchName || "",
            createdAt: new Date().toDateString(),
            totalHours,
          };
        }),
    [employeeDetails, transformedData],
  );

  return (
    <>
      <Paragraph style={{ marginTop: "12px" }}>
        Please take a moment to carefully review your choice before proceeding.
        After you click 'Submit,' the schedule will become accessible to your
        colleagues.
      </Paragraph>

      <div style={{ marginBottom: 16 }}>
        <Checkbox
          onChange={(e) => setNotifyEmployee(e.target.checked)}
          checked={notifyEmployee}
        >
          Notify employee of their schedule?
        </Checkbox>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ marginRight: 8 }}>Task Template (Optional):</label>
        <Select
          placeholder="Select Task Template"
          allowClear
          showSearch
          style={{ width: 300 }}
          optionFilterProp="children"
          value={selectedTaskTemplateId}
          onChange={(value) => setSelectedTaskTemplateId(value)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={taskTemplates.map((task) => ({
            value: task.id,
            label: task.name,
          }))}
        />
      </div>

      <Table
        columns={columns}
        expandable={{
          expandedRowRender: (record) =>
            expandedRowRender(transformedData, record.key),
          onExpand: handleExpand,
          expandedRowKeys,
        }}
        dataSource={rowData}
        size="small"
      />

      {updatedDraftSchedules.length > 0 && (
        <>
          <Paragraph style={{ marginTop: 20, marginBottom: 8 }}>
            Draft schedules marked for update ({updatedDraftSchedules.length})
          </Paragraph>
          <Table
            columns={updateColumns}
            dataSource={updatedDraftSchedules.map((item) => ({
              ...item,
              key: item.scheduleId,
            }))}
            size="small"
            pagination={false}
          />
        </>
      )}

      {deletedDraftSchedules.length > 0 && (
        <>
          <Paragraph style={{ marginTop: 20, marginBottom: 8 }}>
            Draft schedules marked for deletion ({deletedDraftSchedules.length})
          </Paragraph>
          <Table
            columns={deleteColumns}
            dataSource={deletedDraftSchedules.map((item) => ({
              ...item,
              key: item.scheduleId,
            }))}
            size="small"
            pagination={false}
          />
        </>
      )}
    </>
  );
};

export default ScheduleBulkAddSummary;
